// Template 1 — "Aurora" — a premium, original light landing page: floating
// glass nav, animated aurora hero with a tilting glass course card, gradient
// display type, scroll-reveal sections, glass cards and shine CTAs. Fully
// theme-driven off the branding color. Fonts: Sora (display) + Inter (body).
import { Fragment, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, HeroVideo, SOCIAL_ICONS, orderedSections, Reveal, GuaranteeBadge, skillIconD, PolicyLinks, LessonIcon } from "./shared";

const SIGNATURE2 = "#00B3A4";
const SIGNATURE = "#f34330";
const INK = "#0a0f1f";   // deep premium navy for dark bands / footer
const INK2 = "#111a33";

export default function Template1({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies, mirror = false, signature = SIGNATURE, signature2 = SIGNATURE2 }) {
  // `mirror` reverses the two main layout grids (hero + body) so the content and
  // the course card swap sides, while text stays left-aligned (used by Template 7).
  const dir = mirror ? "rtl" : "ltr";
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700;800&display=swap");
  const vid = usePreviewVideo();

  // Palette derived from the single branding color for a cohesive premium look.
  const A = themeColor || signature;
  const AD = shade(A, -0.24);           // darker accent
  const AL = shade(A, 0.42);            // light tint
  const AT = shade(A, -0.14);           // legible-on-light accent text
  const B = themeColor2 || signature2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, AL, AT, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";

  const order = orderedSections(landing.section_order);
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);
  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t1-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t1-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t1-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t1-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t1-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t1-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  // Reorderable content blocks — rendered in the admin-chosen `section_order`.
  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal>
        <Sec id="t1-about" eyebrow="Overview" title="About this course" pal={pal}>
          <div className="prose prose-sm mt-4 max-w-none leading-relaxed text-gray-600"><RichText html={landing.description || course.description} /></div>
        </Sec>
      </Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal>
        <div id="t1-instructor">
          <Sec eyebrow="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
            <div className="relative mt-6 overflow-hidden rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,.05)] sm:p-8">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-50 blur-3xl" style={{ background: rgba(A, 0.14) }} />
              <div className="relative grid items-center gap-8 sm:grid-cols-[200px_1fr]">
                <div className="relative mx-auto w-full max-w-[200px]">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[1.4rem] ring-1 ring-gray-100" style={{ boxShadow: `0 24px 50px -24px ${rgba(A, 0.45)}` }}>
                    {instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-6xl font-[800]" style={{ fontFamily: "'Sora',sans-serif", color: AT, background: rgba(A, 0.08) }}>{instructor.name.charAt(0).toUpperCase()}</span>}
                  </div>
                  <span className="absolute -bottom-3 -right-3 flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: grad, boxShadow: `0 10px 22px ${rgba(A, 0.45)}` }}><Icon d="M4.5 12.75l6 6 9-13.5" className="h-5 w-5" stroke={2.4} /></span>
                </div>
                <div className="text-center sm:text-left">
                  <span className="text-[0.72rem] font-bold uppercase tracking-[0.16em]" style={{ color: AT }}>Course Instructor</span>
                  <h3 className="mt-1.5 text-2xl font-[800] text-gray-900 sm:text-[1.85rem]" style={{ fontFamily: "'Sora',sans-serif" }}>{instructor.name}</h3>
                  <span className="mx-auto mt-3 block h-1 w-12 rounded-full sm:mx-0" style={{ background: grad }} />
                  {instructor.bio && <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-gray-600">{instructor.bio}</p>}
                </div>
              </div>
            </div>
          </Sec>
        </div>
      </Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal>
        <Sec id="t1-skill" eyebrow={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
          <div className="mt-6 grid gap-3.5 sm:grid-cols-2">
            {landing.skill.list.map((s, i) => (
              <div key={i} className="group flex items-start gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_12px_30px_rgba(16,24,40,.08)]">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white transition-transform duration-300 group-hover:scale-110" style={{ background: grad, boxShadow: `0 4px 12px ${rgba(A, 0.35)}` }}><Icon d={skillIconD(landing.skill.icon)} className="h-3.5 w-3.5" stroke={2.2} /></span>
                <span className="text-[15px] leading-snug text-gray-700">{s}</span>
              </div>
            ))}
          </div>
          {landing.skill.long_desc && <div className="rich-content mt-6 text-[15px] leading-relaxed text-gray-600" dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
        </Sec>
      </Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t1-curriculum"><Sec eyebrow={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}><Accordion modules={modules} pal={pal} labels={labels} /></Sec></div></Reveal>
    ),
    testimonial: <div id="t1-reviews"><TestimonialBlock testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <GuaranteeBlock guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t1-faq"><FaqBlock faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="min-h-screen bg-white text-[#0a0f1f]" style={{ fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* NAV — floating glass pill */}
      <div className="sticky top-0 z-50 px-3 pt-3 sm:px-5">
        <nav className="mx-auto max-w-7xl">
          <div className="flex h-14 items-center justify-between gap-3 rounded-2xl border border-white/70 bg-white/70 px-3 pl-4 shadow-[0_10px_40px_rgba(16,24,40,.10)] backdrop-blur-xl">
            <a href="#top" className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: grad, boxShadow: `0 6px 16px ${rgba(A, 0.4)}` }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
              <span className="max-w-[38vw] truncate text-[15px] font-extrabold tracking-tight sm:max-w-xs" style={{ fontFamily: "'Sora',sans-serif" }}>{course.name}</span>
            </a>
            <div className="hidden items-center gap-1 md:flex">
              {navLinks.map((l) => <a key={l.href} href={l.href} className="rounded-lg px-3 py-2 text-[13.5px] font-semibold text-gray-500 transition hover:bg-gray-900/[.04] hover:text-gray-900">{l.label}</a>)}
            </div>
            <button onClick={onEnroll} className="group relative overflow-hidden rounded-xl px-4 py-2 text-sm font-bold text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 6px 18px ${rgba(A, 0.4)}` }}>
              <Shine /> <span className="relative">{ctaLabel}</span>
            </button>
          </div>
        </nav>
      </div>

      {/* HERO — animated aurora on light */}
      <header id="top" className="relative overflow-hidden">
        <AuroraBg A={A} AD={AD} B={B} />
        {header.bg_image ? (
          <>
            <img src={header.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/40 via-white/60 to-white" />
          </>
        ) : null}
        <div dir={dir} className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24 lg:pt-20">
          <div dir="ltr" className="lg:col-span-7">
            <span className="inline-flex animate-fade-in-up items-center gap-2 rounded-full border border-gray-200/80 bg-white/70 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-sm backdrop-blur" style={{ color: AT }}>
              <span className="flex h-1.5 w-1.5 rounded-full" style={{ background: A, boxShadow: `0 0 0 4px ${rgba(A, 0.18)}` }} />
              {statusLabel}
            </span>
            <h1 className="mt-5 animate-fade-in-up text-[2.6rem] font-[800] leading-[1.05] tracking-[-0.04em] text-gray-900 sm:text-6xl lg:text-[4rem]" style={{ fontFamily: "'Sora',sans-serif", animationDelay: "60ms" }}>
              {t.before}{" "}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(120deg, ${A}, ${AD})` }}>{t.accent}</span>{" "}
              {t.after}
            </h1>
            {sub && <p className="mt-6 max-w-xl animate-fade-in-up text-[16px] leading-relaxed text-gray-500 sm:text-[17px]" style={{ animationDelay: "120ms" }}>{sub}</p>}
            <div className="mt-8 flex animate-fade-in-up flex-wrap items-center gap-3" style={{ animationDelay: "180ms" }}>
              <button onClick={onEnroll} className="group relative inline-flex items-center gap-2 overflow-hidden rounded-2xl px-7 py-3.5 text-[15px] font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 14px 34px ${rgba(A, 0.42)}` }}>
                <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#t1-curriculum" className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/70 px-6 py-3.5 text-[15px] font-semibold text-gray-700 backdrop-blur transition hover:border-gray-300 hover:bg-white">{labels.preview || "Preview Course"}</a>
            </div>
            <div className="mt-10 flex animate-fade-in-up flex-wrap items-center gap-x-3 gap-y-2.5" style={{ animationDelay: "240ms" }}>
              {course.star_rating > 0 && <Chip A={A}><Stars value={course.star_rating} empty="#e5e7eb" /> <strong className="ml-1 text-gray-900">{Number(course.star_rating).toFixed(1)}</strong><span className="text-gray-400">/5</span></Chip>}
              {course.total_lessons > 0 && <Chip A={A}><Icon d="M4 6h16M4 12h16M4 18h10" className="h-4 w-4" style={{ color: AT }} /><span className="font-semibold text-gray-700">{course.total_lessons} {labels.lessons || "Lessons"}</span></Chip>}
              {modules.length > 0 && <Chip A={A}><Icon d="M19 11H5m14-7H5a2 2 0 00-2 2v14l4-4h12a2 2 0 002-2V6a2 2 0 00-2-2z" className="h-4 w-4" style={{ color: AT }} /><span className="font-semibold text-gray-700">{modules.length} {labels.modules || "Modules"}</span></Chip>}
              <Chip A={A}><Icon d="M13 10V3L4 14h7v7l9-11h-7z" className="h-4 w-4" style={{ color: AT }} /><span className="font-semibold text-gray-700">Lifetime access</span></Chip>
            </div>
          </div>

          {landing.header?.show !== false && (
            <div dir="ltr" className="relative animate-fade-in-up lg:col-span-5" style={{ animationDelay: "160ms" }}>
              {/* glow */}
              <div className="pointer-events-none absolute -inset-6 rounded-[36px] opacity-70 blur-2xl" style={{ background: `radial-gradient(60% 60% at 50% 40%, ${rgba(A, 0.35)}, transparent 70%)` }} />
              <div className="group relative animate-float rounded-[26px] border border-white/70 bg-white/80 p-3 shadow-[0_30px_80px_-20px_rgba(16,24,40,.35)] backdrop-blur-xl transition-transform duration-500 hover:[transform:perspective(1000px)_rotateX(2deg)_rotateY(-4deg)]">
                <div className="overflow-hidden rounded-[18px]">
                  <HeroVideo video={landing.video} poster={course.course_image} accent={A} className="aspect-video w-full" style={{ background: INK }} onExpand={vid.play} premium />
                </div>
                {/* floating rating chip */}
                {course.star_rating > 0 && (
                  <div className="absolute -left-4 top-6 hidden items-center gap-2 rounded-2xl border border-white/70 bg-white/90 px-3 py-2 shadow-lg backdrop-blur-xl sm:flex">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ background: grad }}><Icon d="M11.48 3.5l2.2 4.46 4.92.72-3.56 3.47.84 4.9L11.48 15l-4.4 2.31.84-4.9L4.36 8.68l4.92-.72z" className="h-4 w-4 fill-current" stroke={0} /></span>
                    <div className="leading-tight"><div className="text-sm font-extrabold text-gray-900">{Number(course.star_rating).toFixed(1)}</div><div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{labels.rating || "Rating"}</div></div>
                  </div>
                )}
                {/* floating lessons chip */}
                {course.total_lessons > 0 && (
                  <div className="absolute -bottom-4 -right-3 hidden items-center gap-2 rounded-2xl border border-white/70 bg-white/90 px-3 py-2 shadow-lg backdrop-blur-xl sm:flex">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: rgba(A, 0.12), color: AT }}><Icon d="M12 6.5C10.5 5.5 8 5 6 5v11c2 0 4.5.5 6 1.5m0-12c1.5-1 4-1.5 6-1.5v11c-2 0-4.5.5-6 1.5m0-12v12" className="h-4 w-4" /></span>
                    <div className="leading-tight"><div className="text-sm font-extrabold text-gray-900">{course.total_lessons}</div><div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{labels.lessons || "Lessons"}</div></div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* fade into body */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
      </header>

      {/* BODY */}
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div dir={dir} className="grid gap-10 lg:grid-cols-3 lg:gap-14">
          <div dir="ltr" className="space-y-16 lg:col-span-2">
            {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
          </div>

          {/* STICKY ENROLL CARD */}
          <aside dir="ltr" className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <Reveal>
                <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_20px_60px_-24px_rgba(16,24,40,.30)]">
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-gray-100">
                    <img src={course.course_image} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    <span className="absolute left-3 top-3 rounded-full bg-white/85 px-3 py-1 text-[11px] font-bold uppercase tracking-wide backdrop-blur" style={{ color: AT }}>{statusLabel}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-baseline gap-2">
                      <span className="bg-clip-text text-4xl font-[800] tracking-tight text-transparent" style={{ backgroundImage: grad, fontFamily: "'Sora',sans-serif" }}>{priceLabel}</span>
                      {isPaid && <span className="text-sm text-gray-400">one-time</span>}
                    </div>
                    <button onClick={onEnroll} className="group relative mt-5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 12px 30px ${rgba(A, 0.4)}` }}>
                      <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    <ul className="mt-6 space-y-3 border-t border-gray-100 pt-5">
                      {[course.total_lessons > 0 && `${course.total_lessons} on-demand ${labels.lessons || "lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "modules"}`, "Full lifetime access", "Learn on any device"].filter(Boolean).map((it, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm text-gray-600"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white" style={{ background: grad }}><Icon d="M5 13l4 4L19 7" className="h-3 w-3" stroke={2.6} /></span>{it}</li>
                      ))}
                    </ul>
                    {isPaid && landing.guarantee?.show && (
                      <div className="mt-5 flex items-start gap-2.5 rounded-2xl border p-3.5 text-[13px] leading-relaxed" style={{ borderColor: rgba(A, 0.25), background: rgba(A, 0.05) }}>
                        <Icon d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" className="mt-0.5 h-5 w-5 shrink-0" style={{ color: AT }} />
                        <p className="text-gray-600"><span className="font-bold text-gray-900">{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> {labels.not_satisfied || "Not satisfied? Get a full refund."}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </div>
          </aside>
        </div>

        {/* FINAL CTA — aurora dark */}
        {landing.cta?.show !== false && (
          <Reveal>
            <section className="relative mt-16 overflow-hidden rounded-[32px] px-6 py-16 text-center sm:px-12" style={{ background: INK2 }}>
              <div className="pointer-events-none absolute inset-0 opacity-90" style={{ backgroundImage: `radial-gradient(50% 60% at 20% 20%, ${rgba(A, 0.5)}, transparent 60%), radial-gradient(50% 60% at 85% 80%, ${rgba(AD, 0.5)}, transparent 60%)`, backgroundSize: "180% 180%", animation: "gradientPan 9s ease infinite" }} />
              <div className="pointer-events-none absolute inset-0 opacity-[0.15]" style={{ backgroundImage: `linear-gradient(${rgba("#ffffff", 0.6)} 1px, transparent 1px), linear-gradient(90deg, ${rgba("#ffffff", 0.6)} 1px, transparent 1px)`, backgroundSize: "44px 44px" }} />
              <div className="relative">
                <h2 className="text-3xl font-[800] tracking-tight text-white sm:text-[2.6rem]" style={{ fontFamily: "'Sora',sans-serif" }}>
                  {landing.cta?.heading
                    ? landing.cta.heading
                    : <>{t.before} <span style={{ color: A }}>{t.accent}</span> {t.after}</>}
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-white/70">{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
                <button onClick={onEnroll} className="group relative mt-8 inline-flex items-center gap-2 overflow-hidden rounded-2xl bg-white px-8 py-4 text-[15px] font-bold shadow-xl transition-transform duration-200 hover:-translate-y-0.5" style={{ color: AD }}>
                  <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </section>
          </Reveal>
        )}
      </div>

      {/* FOOTER */}
      <footer className="relative overflow-hidden" style={{ background: INK }}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${rgba(A, 0.6)}, transparent)` }} />
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 px-4 py-10 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center rounded-xl text-white/70 transition hover:-translate-y-0.5 hover:text-white" style={{ background: "rgba(255,255,255,.07)" }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={A} />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
// Default money-back copy (guruai wording), rendered as rich HTML.
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}
// Sweeping highlight overlay for buttons (uses the `shine` keyframe).
function Shine() {
  return <span className="pointer-events-none absolute inset-y-0 -left-4 w-1/3 animate-shine bg-white/25 blur-md" aria-hidden />;
}
function Chip({ A, children }) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200/80 bg-white/70 px-3.5 py-2 text-[13px] shadow-sm backdrop-blur" style={{ boxShadow: `0 2px 10px ${rgba(A, 0.06)}` }}>{children}</span>;
}
// Animated aurora background used behind the hero (soft blurred color blobs).
function AuroraBg({ A, AD, B }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#fbfcfe] to-white" />
      <div className="absolute -left-24 -top-24 h-96 w-96 animate-float-slow rounded-full opacity-60 blur-3xl" style={{ background: rgba(A, 0.30) }} />
      <div className="absolute right-[-6rem] top-10 h-[26rem] w-[26rem] animate-float rounded-full opacity-50 blur-3xl" style={{ background: rgba(AD, 0.26) }} />
      <div className="absolute right-1/4 top-1/3 h-72 w-72 animate-float-slow rounded-full opacity-40 blur-3xl" style={{ background: rgba(B, 0.10) }} />
      <div className="absolute bottom-[-8rem] left-1/3 h-80 w-80 animate-float-slow rounded-full opacity-40 blur-3xl" style={{ background: rgba(A, 0.20) }} />
      <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: `linear-gradient(${rgba(A, 0.05)} 1px, transparent 1px), linear-gradient(90deg, ${rgba(A, 0.05)} 1px, transparent 1px)`, backgroundSize: "46px 46px", maskImage: "radial-gradient(70% 60% at 50% 30%, #000, transparent 75%)", WebkitMaskImage: "radial-gradient(70% 60% at 50% 30%, #000, transparent 75%)" }} />
    </div>
  );
}

/* ------------------------------- sub-blocks ------------------------------- */
function Sec({ id, eyebrow, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <span className="inline-flex items-center gap-2 text-[0.72rem] font-bold uppercase tracking-[0.14em]" style={{ color: pal.AT }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: pal.B }} />{eyebrow}
      </span>
      <h2 className="mt-2 text-[1.7rem] font-[800] tracking-[-0.035em] text-gray-900 sm:text-[2rem]" style={{ fontFamily: "'Sora',sans-serif" }}>{title}</h2>
      {sub && <p className="mt-2 text-sm font-medium text-gray-400">{sub}</p>}
      <span className="mt-3 block h-1 w-14 rounded-full" style={{ background: `linear-gradient(90deg, ${pal.A}, ${pal.B})` }} />
      {children}
    </section>
  );
}

function Accordion({ modules, pal, labels }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-6 space-y-3">
      {modules.map((m, mi) => {
        const isOpen = open === mi;
        return (
          <div key={mi} className="overflow-hidden rounded-2xl border bg-white transition-all duration-300" style={isOpen ? { borderColor: rgba(pal.A, 0.5), boxShadow: `0 12px 34px ${rgba(pal.A, 0.12)}` } : { borderColor: "#eef0f3" }}>
            <button onClick={() => setOpen(isOpen ? -1 : mi)} className="flex w-full items-center gap-3.5 px-5 py-4 text-left">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold text-white transition" style={isOpen ? { background: pal.grad, boxShadow: `0 6px 16px ${rgba(pal.A, 0.35)}` } : { background: rgba(pal.A, 0.12), color: pal.AT }}>{mi + 1}</span>
              <span className="flex-1 text-[15px] font-bold text-gray-900">{m.name}</span>
              <span className="hidden text-xs font-medium text-gray-400 sm:block">{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
              <Icon d="M19 9l-7 7-7-7" className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : "text-gray-400"}`} style={isOpen ? { color: pal.A } : undefined} />
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
              <div className="overflow-hidden">
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                  {(m.lessons || []).map((l, li) => (
                    <li key={li} className="flex items-center gap-3 px-5 py-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: l.locked ? "#f3f4f6" : rgba(pal.A, 0.1) }}>
                        <LessonIcon lesson={l} color={pal.AT} className="h-3.5 w-3.5" />
                      </span>
                      <span className={`flex-1 truncate text-sm ${l.locked ? "text-gray-400" : "text-gray-700"}`}>{l.title}</span>
                      {l.completed && <span className="text-xs font-semibold" style={{ color: pal.A }}>Done</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TestimonialBlock({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec eyebrow={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="group relative flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_-16px_rgba(16,24,40,.22)]">
              <span className="absolute right-5 top-3 font-serif text-6xl leading-none text-gray-100 transition group-hover:text-gray-200" aria-hidden style={{ color: rgba(pal.A, 0.12) }}>&rdquo;</span>
              <Stars value={t.rating} empty="#e5e7eb" />
              <blockquote className="relative mt-4 flex-1 text-[15px] leading-relaxed text-gray-600">{t.quote}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-gray-100 pt-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold text-white" style={{ background: pal.grad }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-bold text-gray-900">{t.name}</span>{t.role && <span className="block truncate text-xs text-gray-400">{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function GuaranteeBlock({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden rounded-[28px] px-6 py-11 sm:px-10" style={{ background: INK2 }}>
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `radial-gradient(50% 80% at 15% 30%, ${rgba(pal.A, 0.4)}, transparent 60%), radial-gradient(50% 80% at 90% 70%, ${rgba(pal.AD, 0.4)}, transparent 60%)`, backgroundSize: "180% 180%", animation: "gradientPan 10s ease infinite" }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /> : null}
        <div className="relative flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-2xl font-[800] text-white sm:text-[1.9rem]" style={{ fontFamily: "'Sora',sans-serif" }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed text-white/70" dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image ? (
              <img src={guarantee.image} alt="" className="h-44 w-44 object-contain drop-shadow-2xl" />
            ) : (
              <GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-44 w-44 drop-shadow-2xl sm:h-48 sm:w-48" />
            )}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function FaqBlock({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec eyebrow={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-6 space-y-3">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="overflow-hidden rounded-2xl border bg-white transition-all duration-300" style={isOpen ? { borderColor: rgba(pal.A, 0.5), boxShadow: `0 12px 30px ${rgba(pal.A, 0.12)}` } : { borderColor: "#eef0f3" }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-3 px-5 py-4 text-left">
                  <span className="flex-1 text-[15px] font-bold text-gray-900">{f.q}</span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition" style={isOpen ? { background: pal.grad, color: "#fff" } : { background: rgba(pal.A, 0.1), color: pal.AT }}>
                    <Icon d="M12 4v16m8-8H4" className={`h-4 w-4 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
                  </span>
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><div className="px-5 pb-4 text-[15px] leading-relaxed text-gray-600">{f.a}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
